import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { HeadphonesIcon } from 'lucide-react';
import { toast } from 'sonner';
import ReportIssueModal from './support/ReportIssueModal';

const ROLE_LABELS = {
  talent: 'Talent',
  casting_professional: 'Casting Professional',
  production_company: 'Production Company',
  agent: 'Agent',
  industry_service: 'Industry Service',
  crew: 'Crew',
  admin: 'Admin',
};

/**
 * Detects the browser family from the user agent string. Kept intentionally
 * simple — we don't need OS / device / engine precision, just enough for a
 * support agent to know what to troubleshoot.
 */
function detectBrowser(ua) {
  if (!ua) return 'Unknown browser';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return 'Safari';
  return 'Other browser';
}

function buildAttachedContext({ user, location }) {
  const browser = detectBrowser(typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const viewport = typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : 'n/a';
  const device = typeof window !== 'undefined' && window.innerWidth < 768 ? 'Mobile' : 'Desktop';
  const roleLabel = ROLE_LABELS[user?.active_role || user?.role] || 'Member';
  const tierLabel = user?.membership_tier
    ? user.membership_tier.charAt(0).toUpperCase() + user.membership_tier.slice(1)
    : 'Free';
  return {
    page: location?.pathname || '/',
    role: roleLabel,
    plan: tierLabel,
    device,
    viewport,
    browser,
  };
}

/**
 * Opens the existing support-ticket modal (POST /api/support/issues) so
 * the user files a proper support ticket that admins manage through their
 * activity feed.
 *
 * Previously this button created a 1:1 DM conversation with the admin
 * user (POST /api/conversations + recipient_id=admin). That mixed support
 * with normal user-to-user messaging, polluted admin inboxes, and meant
 * tickets were never logged in `db.support_issues` for the admin UI to
 * triage. Fixed in 2026-05-13: this button now opens the ReportIssueModal
 * which writes to the canonical support_issues collection.
 *
 * Props:
 *   attachContext — set to false to skip auto-attaching page/role/device
 *                   context to the ticket (default true).
 */
export default function ContactSupportButton({
  variant = 'default',
  size = 'default',
  className = '',
  showLabel = true,
  attachContext = true,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    if (!user) {
      toast.error('Please sign in to contact support');
      navigate('/login');
      return;
    }
    setOpen(true);
  };

  const initialContext = attachContext ? buildAttachedContext({ user, location }) : null;

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={handleClick}
        data-testid="contact-support-btn"
      >
        <HeadphonesIcon className="w-4 h-4 mr-2" />
        {showLabel ? 'Contact Support' : ''}
      </Button>

      {open && (
        <ReportIssueModal
          onClose={() => setOpen(false)}
          initialContext={initialContext}
        />
      )}
    </>
  );
}
